import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/password-reset`,
      });
      
      if (error) {
        toast.error(error.message);
      } else {
        setEmailSent(true);
        toast.success('Password reset email sent! Check your inbox.');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <Layout>
        <div className="max-w-md mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h2 className="text-slate-950 font-medium text-lg">
              Check Your Email
            </h2>
            <p className="text-slate-600 text-sm mt-2">
              We've sent a password reset link to {email}
            </p>
          </div>

          <Card className="border-0 shadow-none">
            <div className="p-6 text-center">
              <p className="text-slate-600 mb-4">
                Click the link in your email to reset your password. 
                The link will expire in 1 hour.
              </p>
              <Link to="/profile">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Login
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-slate-950 font-medium text-lg">
            Reset Your Password
          </h2>
          <p className="text-slate-600 text-sm mt-2">
            Enter your email address and we'll send you a link to reset your password
          </p>
        </div>

        <Card className="border-0 shadow-none">
          <form onSubmit={handleResetPassword} className="space-y-4 p-6">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email address"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
            
            <div className="text-center">
              <Link 
                to="/profile" 
                className="text-emerald-600 hover:text-emerald-500 text-sm"
              >
                <ArrowLeft className="h-4 w-4 inline mr-1" />
                Back to Login
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </Layout>
  );
};

export default ForgotPassword;